import React, { useState, useContext, useEffect, useRef } from 'react';
import { GlobalContext } from 'providers/GlobalProvider';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { styled } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Checkbox from '@mui/material/Checkbox';
import OutlinedInput from '@mui/material/OutlinedInput';
import { CircularProgress } from '@mui/material';

interface IModalCommon {
  cur_user: any;
  handleClose?: any;
  sharedArr: any[];
  owner: string;
  addShared: any;
  mode?: 'share' | 'send';
  handleSend?: (payload: { name: string; emails: string[] }) => void;
  triggerSend?: boolean;
  isOpen?: boolean;
}

const CustomSelect = styled(Select)({
  '.MuiSelect-icon': {
    color: 'var(--card-text)',
  },
  '.MuiOutlinedInput-input': {
    minHeight: '48px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    padding: '10px 14px',
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--Colors-Border-border-primary, var(--bg-border-db))',
  },
  overflow: 'hidden',
  color: 'var(--Text-Main, var(--text-primary))',
  textOverflow: 'ellipsis',
  fontFamily: 'Inter',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
  borderRadius: '8px',
  minHeight: '48px',
  background: 'var(--bg-primary)',
});

const AddPersonModal: React.FC<IModalCommon> = ({
  cur_user,
  handleClose,
  sharedArr,
  owner,
  addShared,
  mode = 'share',
  handleSend,
  triggerSend = false,
  isOpen = false,
}) => {
  const [shareEmail, setShareEmail] = useState<string[]>([]);
  const [activeMode, setActiveMode] = useState<'share' | 'send'>(mode);
  const [sendFileName, setSendFileName] = useState('');
  const [sendFormValues, setSendFormValues] = useState<{
    email: string;
    emails: string[];
  }>({
    email: '',
    emails: [],
  });
  const { userOpts } = useContext(GlobalContext);
  const [shareOptions, setShareOptions] = useState<any[]>([]);
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  const resetModalState = () => {
    setShareEmail([]);
    setSendFileName('');
    setSendFormValues({
      email: '',
      emails: [],
    });
  };

  const handleShare = async () => {
    if (shareEmail.length > 0 && !triggerSend) {
      addShared(shareEmail);
      handleClose();
    }
  };

  const handleShareChange = (e: any) => {
    const value =
      typeof e.target.value === 'string'
        ? e.target.value.split(',')
        : e.target.value;

    if (value.length <= 20) {
      setShareEmail(value);
    }
  };

  const filterUserOpts = (str: string, arr: string[]) => {
    const blocked = [str, ...arr, cur_user?.basics?.email];
    setShareOptions(
      userOpts.filter((opt: any) => !blocked.includes(opt.label)),
    );
  };

  const normalizeEmails = (value: string) => {
    return value
      .split(/[\s,;]+/)
      .map((email) => email.trim())
      .filter(Boolean);
  };

  const handleAddEmail = () => {
    const nextEmails = normalizeEmails(sendFormValues.email);

    if (nextEmails.length === 0) {
      emailInputRef.current?.focus();
      return;
    }

    setSendFormValues((prev: any) => {
      const existingEmails = new Set(
        (prev.emails || []).map((email: string) => email.toLowerCase()),
      );
      const uniqueEmails = nextEmails.filter(
        (email) => !existingEmails.has(email.toLowerCase()),
      );

      return {
        ...prev,
        email: '',
        emails: [...(prev.emails || []), ...uniqueEmails],
      };
    });
    emailInputRef.current?.focus();
  };

  const handleRemoveEmail = (index: number) => {
    setSendFormValues((prev: any) => ({
      ...prev,
      emails: prev.emails.filter(
        (_: string, emailIndex: number) => emailIndex !== index,
      ),
    }));
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddEmail();
      return;
    }

    if (
      e.key === 'Backspace' &&
      sendFormValues.email === '' &&
      sendFormValues.emails.length > 0
    ) {
      setSendFormValues((prev: any) => ({
        ...prev,
        emails: prev.emails.slice(0, -1),
      }));
    }
  };

  const handleSendSubmit = () => {
    if (triggerSend) {
      return;
    }

    const emails =
      sendFormValues.email.trim() !== ''
        ? [...sendFormValues.emails, ...normalizeEmails(sendFormValues.email)]
        : sendFormValues.emails;

    handleSend?.({
      name: sendFileName,
      emails: Array.from(new Set(emails.map((email) => email.trim()))),
    });
  };

  useEffect(() => {
    filterUserOpts(owner, sharedArr);
  }, [owner, sharedArr, userOpts]);

  useEffect(() => {
    setActiveMode(mode);
  }, [mode]);

  useEffect(() => {
    if (!isOpen) {
      resetModalState();
    }
  }, [isOpen]);

  return (
    <div className='audit-add-modal-cont'>
      <div className='audit-add-modal-cont-head'>
        <div className='audit-add-modal-head-cont'>
          <div className='audit-add-modal-head-cont-icon'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
            >
              <path
                d='M8.59 13.51L15.42 17.49M15.41 6.51L8.59 10.49M21 5C21 6.65685 19.6569 8 18 8C16.3431 8 15 6.65685 15 5C15 3.34315 16.3431 2 18 2C19.6569 2 21 3.34315 21 5ZM9 12C9 13.6569 7.65685 15 6 15C4.34315 15 3 13.6569 3 12C3 10.3431 4.34315 9 6 9C7.65685 9 9 10.3431 9 12ZM21 19C21 20.6569 19.6569 22 18 22C16.3431 22 15 20.6569 15 19C15 17.3431 16.3431 16 18 16C19.6569 16 21 17.3431 21 19Z'
                stroke='#4895EF'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <div className='audit-add-modal-head-cont-supporting'>
            <h1>Share Results</h1>
            <p>
              {activeMode === 'share'
                ? 'Share and manage who can see this.'
                : 'Send the result PDF to recipients.'}
            </p>
          </div>
        </div>
        <div
          className='audit-add-modal-head-close'
          onClick={() => {
            if (triggerSend) {
              return;
            }
            resetModalState();
            handleClose();
          }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
          >
            <path
              d='M18 6L6 18M6 6L18 18'
              stroke='#85888E'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </div>
      <div className='audit-add-modal-cont-body'>
        <div className='audit-add-modal-cont-body-frame'>
          <div className='audit-add-modal-cont-body-frame-form'>
            <div className='audit-add-modal-cont-body-frame-form-row'>
              <div className='audit-add-modal-cont-body-frame-form-row-input'>
                <div className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                  Action
                </div>
                <div className='share-send-option-row'>
                  <div
                    className='bill-container-fit-screen-content-row-checkbox'
                    onClick={() => setActiveMode('share')}
                  >
                    <div className='bill-container-fit-screen-content-row-input'>
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon ${
                          activeMode === 'share' ? 'selected' : ''
                        }`}
                      >
                        <div
                          className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                            activeMode === 'share' ? 'selected' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className='bill-container-fit-screen-content-row-checkbox-text'>
                      <p>Share in App</p>
                    </div>
                  </div>
                  <div
                    className='bill-container-fit-screen-content-row-checkbox'
                    onClick={() => setActiveMode('send')}
                  >
                    <div className='bill-container-fit-screen-content-row-input'>
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon ${
                          activeMode === 'send' ? 'selected' : ''
                        }`}
                      >
                        <div
                          className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                            activeMode === 'send' ? 'selected' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className='bill-container-fit-screen-content-row-checkbox-text'>
                      <p>Email PDF</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='audit-add-modal-cont-body-frame'>
          <div className='audit-add-modal-cont-body-frame-form'>
            <div className='audit-add-modal-cont-body-frame-form-row'>
              <div className='audit-add-modal-cont-body-frame-form-row-input'>
                {activeMode === 'share' ? (
                  <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                    <div className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                      Add User
                    </div>
                    <FormControl sx={{ width: '430px' }}>
                      <CustomSelect
                        multiple
                        value={shareEmail}
                        onChange={handleShareChange}
                        input={<OutlinedInput />}
                        fullWidth
                        IconComponent={KeyboardArrowDownIcon}
                        renderValue={(selected: string[]) =>
                          selected.join(', ')
                        }
                        inputProps={{
                          MenuProps: {
                            MenuListProps: {
                              sx: {
                                backgroundColor: 'var(--bg-colors)',
                                color: 'var(--Text-Main, var(--text-primary))',
                              },
                            },
                          },
                        }}
                      >
                        {shareOptions.map((user: any, i: number) => (
                          <MenuItem key={i} value={user.label}>
                            <Checkbox checked={shareEmail.indexOf(user.label) > -1} />
                            {user.label}
                          </MenuItem>
                        ))}
                      </CustomSelect>
                    </FormControl>
                  </div>
                ) : (
                  <>
                    <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                      <input
                        className='audit-add-modal-cont-body-frame-form-input'
                        type='text'
                        placeholder='e.g. Oligarch'
                        value={sendFileName}
                        onChange={(e) => setSendFileName(e.target.value)}
                      />
                    </div>
                    <div
                      className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      style={{ marginTop: '12px' }}
                    >
                      Email
                    </div>
                    <div
                      className='invite-users-input-and-drop'
                      style={{ width: '100%' }}
                    >
                      <div
                        className='team-members-chip-input invite-user-modal-chip-input'
                        onClick={() => emailInputRef.current?.focus()}
                      >
                        <div className='team-members-chip-list'>
                          {sendFormValues.emails.map(
                            (email: string, index: number) => (
                              <div
                                key={`send-tariff-email-${index}`}
                                className='team-member-chip'
                              >
                                <div className='team-member-chip-text'>
                                  {email}
                                </div>
                                <button
                                  type='button'
                                  className='team-member-remove-btn'
                                  onClick={() => handleRemoveEmail(index)}
                                  aria-label={`Remove send PDF email ${index + 1}`}
                                >
                                  x
                                </button>
                              </div>
                            ),
                          )}
                          <input
                            ref={emailInputRef}
                            id='send-tariff-email'
                            placeholder={
                              sendFormValues.emails.length === 0
                                ? 'email@example.com'
                                : ''
                            }
                            type='text'
                            className='team-members-chip-input-field'
                            name='email'
                            value={sendFormValues.email}
                            onChange={(e) =>
                              setSendFormValues({
                                ...sendFormValues,
                                email: e.target.value,
                              })
                            }
                            onKeyDown={handleEmailKeyDown}
                          />
                        </div>
                      </div>
                    </div>
                    <div
                      className='team-members-add-btn'
                      onClick={handleAddEmail}
                    >
                      + Add another member
                    </div>
                    <div className='team-members-helper-text'>
                      Press Enter or click add to append multiple emails into
                      the same field.
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {activeMode === 'share' && (
        <div className='audit-add-modal-contents'>
          <h1>People With Access</h1>
          <div className='audit-add-modal-contents-frame'>
            <div className='audit-add-modal-contents-frame-cell'>
              <div className='audit-add-modal-contents-frame-cell-name'>
                {owner}
              </div>
              <div className='audit-add-modal-contents-frame-cell-perm'>
                Owner
              </div>
            </div>
            {sharedArr.map((member: string, i: number) => (
              <div key={i} className='audit-add-modal-contents-frame-cell'>
                <div className='audit-add-modal-contents-frame-cell-name'>
                  {member}
                </div>
                <div className='audit-add-modal-contents-frame-cell-perm'>
                  Contributor
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className='audit-modal-cont-footer'>
        <div className='audit-modal-cont-footer-content'>
          <div
            className='audit-modal-cont-footer-content-btn-cancel'
            onClick={() => {
              if (triggerSend) {
                return;
              }
              resetModalState();
              handleClose();
            }}
          >
            <div className='audit-modal-cont-footer-content-btn-text'>
              Cancel
            </div>
          </div>
          <div
            className='audit-modal-cont-footer-content-btn-upload'
            onClick={activeMode === 'share' ? handleShare : handleSendSubmit}
          >
            <div
              className='audit-modal-cont-footer-content-btn-text'
              style={{ color: '#fff' }}
            >
              {triggerSend && activeMode === 'send' && (
                <CircularProgress
                  style={{
                    width: '20px',
                    height: '20px',
                    marginRight: '10px',
                  }}
                  color='primary'
                />
              )}
              {activeMode === 'share' ? 'Share' : 'Send PDF'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPersonModal;
